/**
 * Domain enums shared by API and web. Values are stored verbatim in PostgreSQL,
 * so treat them as a stable contract.
 */

export const ProductStatus = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived',
} as const;
export type ProductStatus = (typeof ProductStatus)[keyof typeof ProductStatus];

/**
 * Product type drives the category-specific customer experience and the
 * category-specific extension fields (spec sections 6 & 17). New creative
 * categories must be addable without schema changes.
 */
export const ProductType = {
  STANDARD: 'standard',
  PRINT_3D: 'print_3d',
  DIY_KIDS_KIT: 'diy_kids_kit',
  CERAMIC_MOULD: 'ceramic_mould',
  GYPSUM_CONCRETE_MOULD: 'gypsum_concrete_mould',
  CANDLE: 'candle',
  CANDLE_MOULD: 'candle_mould',
  CRAFT_SUPPLY: 'craft_supply',
  CUSTOM_ORDER: 'custom_order',
} as const;
export type ProductType = (typeof ProductType)[keyof typeof ProductType];

export const MediaType = {
  IMAGE: 'image',
  VIDEO: 'video',
  DOWNLOADABLE: 'downloadable',
} as const;
export type MediaType = (typeof MediaType)[keyof typeof MediaType];

export const MediaStatus = {
  PENDING: 'pending',
  ACTIVE: 'active',
  QUARANTINED: 'quarantined',
} as const;
export type MediaStatus = (typeof MediaStatus)[keyof typeof MediaStatus];

/** Immutable stock ledger movement types (spec section 7). */
export const StockMovementType = {
  INITIAL: 'INITIAL',
  RESTOCK: 'RESTOCK',
  SALE: 'SALE',
  ORDER_CANCEL_RELEASE: 'ORDER_CANCEL_RELEASE',
  RETURN: 'RETURN',
  DAMAGE: 'DAMAGE',
  MANUAL_ADJUSTMENT: 'MANUAL_ADJUSTMENT',
  TRANSFER: 'TRANSFER',
} as const;
export type StockMovementType = (typeof StockMovementType)[keyof typeof StockMovementType];

/** Order lifecycle (spec section 12). Payment + fulfillment are separate fields. */
export const OrderStatus = {
  PENDING_PAYMENT: 'PENDING_PAYMENT',
  PAID: 'PAID',
  PROCESSING: 'PROCESSING',
  READY_TO_SHIP: 'READY_TO_SHIP',
  SHIPPED: 'SHIPPED',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  FAILED: 'FAILED',
} as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

export const PaymentStatus = {
  PENDING: 'PENDING',
  AUTHORIZED: 'AUTHORIZED',
  PAID: 'PAID',
  FAILED: 'FAILED',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const PaymentProvider = {
  MANUAL: 'manual',
  COD: 'cod',
  RAZORPAY: 'razorpay',
} as const;
export type PaymentProvider = (typeof PaymentProvider)[keyof typeof PaymentProvider];

/** What the customer picks at checkout. "online" resolves to the configured provider. */
export const PaymentMethodChoice = {
  ONLINE: 'online',
  COD: 'cod',
} as const;
export type PaymentMethodChoice = (typeof PaymentMethodChoice)[keyof typeof PaymentMethodChoice];

export const FulfillmentStatus = {
  UNFULFILLED: 'UNFULFILLED',
  PROCESSING: 'PROCESSING',
  READY_TO_SHIP: 'READY_TO_SHIP',
  PARTIALLY_SHIPPED: 'PARTIALLY_SHIPPED',
  SHIPPED: 'SHIPPED',
  DELIVERED: 'DELIVERED',
  RETURNED: 'RETURNED',
} as const;
export type FulfillmentStatus = (typeof FulfillmentStatus)[keyof typeof FulfillmentStatus];

export const CustomOrderStatus = {
  SUBMITTED: 'SUBMITTED',
  IN_REVIEW: 'IN_REVIEW',
  QUOTED: 'QUOTED',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
  CONVERTED: 'CONVERTED',
  CLOSED: 'CLOSED',
} as const;
export type CustomOrderStatus = (typeof CustomOrderStatus)[keyof typeof CustomOrderStatus];

/** Banner placements (spec section 11). */
export const BannerPlacement = {
  HOME_HERO: 'home_hero',
  HOME_SECONDARY: 'home_secondary',
  CATEGORY_HERO: 'category_hero',
  CAMPAIGN: 'campaign',
} as const;
export type BannerPlacement = (typeof BannerPlacement)[keyof typeof BannerPlacement];

/** RBAC roles (spec section 5). */
export const Role = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  CATALOG_MANAGER: 'CATALOG_MANAGER',
  INVENTORY_MANAGER: 'INVENTORY_MANAGER',
  ORDER_MANAGER: 'ORDER_MANAGER',
  CONTENT_MANAGER: 'CONTENT_MANAGER',
  VIEWER_ANALYST: 'VIEWER_ANALYST',
} as const;
export type Role = (typeof Role)[keyof typeof Role];

export const AnalyticsEvent = {
  PAGE_VIEW: 'page_view',
  VIEW_ITEM: 'view_item',
  VIEW_ITEM_LIST: 'view_item_list',
  SEARCH: 'search',
  ADD_TO_CART: 'add_to_cart',
  REMOVE_FROM_CART: 'remove_from_cart',
  BEGIN_CHECKOUT: 'begin_checkout',
  PURCHASE: 'purchase',
  BANNER_CLICK: 'banner_click',
  CUSTOM_ORDER_SUBMIT: 'custom_order_submit',
  WISHLIST_ADD: 'wishlist_add',
} as const;
export type AnalyticsEvent = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/** Storefront availability mode, flipped from the admin panel. */
export const SiteMode = {
  LIVE: 'live',
  COMING_SOON: 'coming_soon',
  MAINTENANCE: 'maintenance',
} as const;
export type SiteMode = (typeof SiteMode)[keyof typeof SiteMode];

export const WeightUnit = { G: 'g', KG: 'kg' } as const;
export type WeightUnit = (typeof WeightUnit)[keyof typeof WeightUnit];

export const LengthUnit = { MM: 'mm', CM: 'cm' } as const;
export type LengthUnit = (typeof LengthUnit)[keyof typeof LengthUnit];
